import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";

import { sendVerificationEmail } from "../../helpers/firebaseAuth";
import { useAuthUser } from "../../hooks/AuthProvider";
import { useAuthStatus } from "../../hooks/useAuthStatus";
import { ROLE_HOMES, ROLE_TENANT } from "../../context/RoleContext";

import profileImg from "../../assets/images/profile-img.png";
import porcherMark from "../../assets/images/porcher-logo.png";

const VerifyEmail = () => {
  document.title = "Verify Email | Porcher";

  const user = useAuthUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuthStatus();

  // Falls back to whichever role was picked at signup/login, not a hardcoded dashboard.
  const returnUrl = searchParams.get("returnUrl")
    || ROLE_HOMES[localStorage.getItem("ezron.activeRole")]
    || ROLE_HOMES[ROLE_TENANT];

  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleRefresh = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const authState = await checkAuth();
      if (authState?.isEmailVerified) {
        navigate(returnUrl, { replace: true });
        return;
      }

      setSuccess("Still waiting on verification. Open the email we sent, then click refresh.");
    } catch {
      setError("Could not refresh your verification status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setSuccess("");
    setResending(true);

    try {
      await sendVerificationEmail(user);
      setSuccess("Verification email sent. Check your inbox and spam folder.");
    } catch (err) {
      if (err?.code === "auth/too-many-requests") {
        setError("Too many requests. Please wait a minute before resending.");
      } else {
        setError("Could not send verification email. Please try again.");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <React.Fragment>
      <div className="home-btn d-none d-sm-block">
        <Link to="/" className="text-dark">
          <i className="bx bx-home h2" />
        </Link>
      </div>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col className="col-7">
                      <div className="text-primary p-4">
                        <h5 className="text-primary mb-2">Verify your email</h5>
                        <p className="mb-0">We sent a confirmation link to finish setting up your account.</p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profileImg} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div className="auth-logo">
                    <Link to="/" className="auth-logo-light">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img src={porcherMark} alt="Porcher" className="rounded-circle" height="34" />
                        </span>
                      </div>
                    </Link>
                    <Link to="/" className="auth-logo-dark">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img src={porcherMark} alt="Porcher" className="rounded-circle" height="34" />
                        </span>
                      </div>
                    </Link>
                  </div>

                  <div className="p-2">
                    {success && <Alert color="success">{success}</Alert>}
                    {error && <Alert color="danger">{error}</Alert>}

                    <div className="text-center mb-4">
                      <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: 72, height: 72, background: "#eff2ff", color: "#4f46e5" }}
                      >
                        <i className="bx bx-envelope-open" style={{ fontSize: "2rem" }} />
                      </div>
                      <h4 className="mb-2">Check your inbox</h4>
                      <p className="text-muted mb-0">
                        {user?.email || "Your email address"} needs to be verified before you can continue.
                      </p>
                    </div>

                    <div className="d-grid gap-2">
                      <Button color="primary" onClick={handleRefresh} disabled={loading}>
                        {loading ? <Spinner size="sm" className="me-2" /> : <i className="bx bx-refresh me-2" />}
                        I verified my email
                      </Button>
                      <Button color="light" onClick={handleResend} disabled={resending}>
                        {resending ? <Spinner size="sm" className="me-2" /> : <i className="bx bx-mail-send me-2" />}
                        Resend verification email
                      </Button>
                    </div>

                    <p className="text-muted small mt-4 mb-0 text-center">
                      If you used Google sign-in, you will not see this step. Password-based accounts must verify their email first.
                    </p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default VerifyEmail;